<template>
    <header class="header-section shadow-sm" :style="{ backgroundColor: headerBgColor, boxShadow: headerBoxShadow }">
        <!-- 全局搜索弹出层，仅在非扩展搜索状态下可能显示 -->
        <div v-if="isSearchOpen && !isSearchExpanded" class="fixed inset-0 bg-opacity-98 z-50 flex items-center justify-center p-4">
            <div class="w-full max-w-2xl h-[78px]">
                <div class="flex items-center justify-between mb-6">
                    <h3 class="text-xl font-weight-600 text-gray-700">Search</h3>
                    <button @click="toggleSearch" class="text-gray-500 hover:text-gray-800 focus:outline-none" aria-label="Close search">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="relative">
                    <input type="text" v-model="searchText" placeholder="Search for products, solutions, etc..."
                        class="w-full px-4 py-3 border-2 border-green-600 rounded-l-full focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500" aria-label="Search input" />
                    <button class="absolute right-0 top-0 h-full bg-green-600 text-white p-2 px-4 rounded-r-full rounded-l-none hover:bg-green-700" aria-label="Submit search">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Desktop Header -->
        <div class="hidden lg:flex w-full ">
            <!-- Full width container with padding on both sides -->
            <div class="flex items-center justify-between w-full px-6  h-[98px]">
                <!-- Logo (Left edge aligned) -->
                <div class="logo">
                    <nuxt-link to="/" class="flex items-center">
                        <img src="https://images.leaflypackaging.com/profile/upload/2026/01/29/logo_20260129164152A002.webp" alt="Leafly Packaging" class="h-[3.75rem]" style="width:100%" />
                    </nuxt-link>
                </div>

                <!-- Center Navigation -->
                <nav class="flex justify-center flex-1 h-100">
                    <div class="flex items-center space-x-10">
                        <div v-for="(navItem, index) in navItemList" :key="index" class="relative group py-6">
                            <nuxt-link :to="navItem.linkUrl" class="flex items-center uppercase  text-[1rem] font-weight-600 hover:text-green-600 nav-link" :class="{
                                'text-green-600 font-weight-600 active-link': navItem.active,
                                'text-white': scrollOpacity < 0.5 && !navItem.active,
                                'text-gray-800': scrollOpacity >= 0.5 && !navItem.active
                            }">
                                {{ navItem.name }}
                                <span v-if="navItem.children && navItem.children.length > 0" class="ml-1.5" aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </span>
                            </nuxt-link>

                            <!-- Submenu -->
                            <div v-if="navItem.children && navItem.children.length > 0"
                                class="absolute top-full left-0 bg-white shadow-sm border-t border-gray-100 py-3 w-64 z-50 hidden group-hover:block">
                                <div class="bg-white">
                                    <nuxt-link v-for="(subItem, subIndex) in navItem.children" :key="subIndex" :to="subItem.linkUrl"
                                        class="block px-5 py-2.5 text-[1rem] font-weight-600 text-gray-700 hover:bg-green-50 hover:text-green-600 nav-link">
                                        {{ subItem.name }}
                                    </nuxt-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>

                <!-- Contact Us button and Search (Right edge aligned) -->
                <div class="flex items-center space-x-3">
                    <!-- Search Input & Button -->
                    <div class="relative flex items-center">
                        <div class="absolute right-0 flex items-center bg-white overflow-hidden search-input-container"
                            :class="{ 'search-expanded': isSearchExpanded, 'search-collapsed': !isSearchExpanded }">
                            <input ref="searchInput" type="text" v-model="searchText" placeholder="Search..."
                                class="w-full px-4 py-2 border-2 border-green-600 rounded-l-full outline-none text-gray-700 focus:border-green-600" aria-label="Search products" />
                        </div>
                        <button @click="toggleSearchInput" class="search-button text-white focus:outline-none bg-green-600 hover:bg-green-700 p-2.5 rounded-full z-10" aria-label="Toggle search">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </button>
                    </div>
                    <!-- Contact Us Button -->
                    <nuxt-link to="/contact-us" class="bg-green-600 hover:bg-green-700 text-white font-weight-600 py-2.5 px-6 rounded uppercase">CONTACT US</nuxt-link>
                </div>
            </div>
        </div>

        <!-- Mobile Header -->
        <div class="lg:hidden flex items-center justify-between py-4 px-6 border-gray-100">
            <!-- Logo -->
            <div class="logo">
                <nuxt-link to="/" class="flex items-center">
                    <img src="https://images.leaflypackaging.com/profile/upload/2026/01/29/logo_20260129164152A002.webp" alt="Leafly Packaging" class="h-10" style="width:100%" />
                </nuxt-link>
            </div>

            <!-- Mobile Search Input (Expanded) -->
            <div v-if="isSearchExpanded" class="fixed inset-0 bg-white bg-opacity-95 z-50 flex flex-col p-4">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="font-weight-600 text-lg">Search</h3>
                    <button @click="toggleSearchInput" class="text-gray-500 focus:outline-none" aria-label="Close search">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="relative">
                    <input type="text" v-model="searchText" placeholder="Search..."
                        class="w-full px-4 py-3 border-2 border-green-600 rounded-l-full focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500" aria-label="Search input" />
                    <button class="absolute right-0 top-0 h-full bg-green-600 text-white p-2 px-4 rounded-r-full rounded-l-none hover:bg-green-700" aria-label="Submit search">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Mobile Menu & Search Buttons -->
            <div class="flex items-center space-x-4">
                <!-- Search Button -->
                <button @click="toggleSearchInput" class="search-button-mobile text-white focus:outline-none bg-green-600 p-2 rounded-full" aria-label="Open search">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </button>

                <!-- Menu Button -->
                <button @click="toggleMobileMenu" class="text-gray-600 focus:outline-none" :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'">
                    <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Mobile Menu -->
        <div v-show="mobileMenuOpen" class="lg:hidden py-2 px-6 border-b border-gray-100 shadow-sm mobileMenuOpen">
            <div v-for="(navItem, index) in navItemList" :key="index" class="border-b border-gray-100 last:border-0">
                <div @click="toggleMobileSubmenu(index)" class="flex items-center justify-between cursor-pointer uppercase text-[1rem] py-3.5" :class="{
                    'text-white hover:text-green-400': scrollOpacity < 0.5,
                    'text-gray-800 hover:text-green-600': scrollOpacity >= 0.5
                }">
                    <nuxt-link v-if="!navItem.children || navItem.children.length === 0" :to="navItem.linkUrl" class="block w-full nav-link">
                        {{ navItem.name }}
                    </nuxt-link>
                    <span v-else>{{ navItem.name }}</span>
                    <span v-if="navItem.children && navItem.children.length > 0" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform duration-300" :class="{ 'rotate-180': expandedMenus.includes(index) }" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </span>
                </div>

                <!-- Mobile Submenu -->
                <div v-if="navItem.children && navItem.children.length > 0 && expandedMenus.includes(index)" class="pl-4 py-1 bg-gray-50">
                    <nuxt-link v-for="(subItem, subIndex) in navItem.children" :key="subIndex" :to="subItem.linkUrl"
                        class="block py-2.5 text-[1rem] text-gray-700 hover:text-green-600 border-t border-gray-100 first:border-0 nav-link">
                        {{ subItem.name }}
                    </nuxt-link>
                </div>
            </div>
            <div class="mt-5 pb-3">
                <nuxt-link to="/contact-us" class="block w-full py-2.5 px-4 text-center bg-green-600 text-white rounded uppercase font-weight-600">Contact Us</nuxt-link>
            </div>
        </div>
    </header>
</template>
<script setup lang="ts">
    import cmsConfig from '~/cmsConfig';
    import cmsUtils from '~/utils/cmsUtils';
    import type { NavItem } from '~/types';
    import { ref, onMounted, onUnmounted, computed } from 'vue';

    const isSearchOpen = ref(false)
    const isSearchExpanded = ref(false) // 搜索框是否展开
    const searchText = ref('') // 搜索文本
    const activeNvItem = ref(0) // 当前活跃菜单项
    const searchInput = ref<HTMLInputElement | null>(null) // 搜索输入框引用
    const mobileMenuOpen = ref(false)
    const expandedMenus = ref<number[]>([])
    // 服务端从缓存获取，客户端通过 API 获取
    const cmsInfo = await cmsUtils.getCmsInfo()
    const isScrolled = ref(false) // 是否已滚动超过100px (已保留用于其他可能逻辑)
    const scrollOpacity = ref(0) // 背景透明度 0-1

    const headerBgColor = ref('rgba(255, 255, 255, 0)') // 动态背景色
    const headerBoxShadow = ref('none') // 动态阴影

    function toggleSearch() {
        isSearchOpen.value = !isSearchOpen.value
        // 这里可以添加更多搜索功能相关的逻辑
    }

    // 切换搜索输入框的展开/收起状态
    function toggleSearchInput() {
        isSearchExpanded.value = !isSearchExpanded.value

        // 如果展开搜索框，则自动聚焦到输入框
        if (isSearchExpanded.value) {
            setTimeout(() => {
                searchInput.value?.focus()
            }, 300)
        } else {
            // 如果收起搜索框，则清空搜索内容
            searchText.value = ''
        }
    }

    // 菜单和搜索控制
    function toggleMobileMenu() {
        mobileMenuOpen.value = !mobileMenuOpen.value
    }

    function toggleMobileSubmenu(index: number) {
        const position = expandedMenus.value.indexOf(index)
        if (position > -1) {
            expandedMenus.value.splice(position, 1)
        } else {
            expandedMenus.value.push(index)
        }
    }

    // 监听滚动事件，根据滚动位置计算透明度
    function handleScroll() {
        const scrollY = window.scrollY
        const maxScroll = 600 // 600px时完全显示

        isScrolled.value = scrollY > 100

        // 计算透明度：0-600px之间，从0到1
        const opacity = Math.min(scrollY / maxScroll, 1)

        // 更新背景透明度
        scrollOpacity.value = opacity

        // 设置背景色透明度
        headerBgColor.value = `rgba(255, 255, 255, ${opacity})`

        // 设置阴影：当有透明度时才显示阴影
        if (opacity > 0) {
            headerBoxShadow.value = `0 2px 10px rgba(0, 0, 0, ${0.1 * opacity})`
        } else {
            headerBoxShadow.value = 'none'
        }
    }

    onMounted(() => {
        window.addEventListener('scroll', handleScroll)
        // 初始化时检查一次滚动位置
        handleScroll()
    })

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
    })

    const navItemList = ref<NavItem[]>(cmsUtils.getNavItemTreeByNavIndex("header_main", cmsInfo))
    const routerPath = useRoute().path

    // 让一级导航的active的状态
    navItemList.value.forEach(item => {
        if (!item.linkUrl) {
            item.active = false;
            return
        }

        if (item.linkUrl == '/' && routerPath !== '/') {
            item.active = false;
            return
        }

        // 因为violetjar 在 packaging 中，会出现packaging 和 violet 都是actvie，所以需要这里需要处理一下，让packaging 不要active
        // if(routerPath.includes('/violet-jar.html') && item.linkUrl?.includes('/packaging')){
        //     item.active = false
        //     return;
        // }

        if (routerPath.includes(item.linkUrl!.replace('.html', ''))) {
            item.active = true
            return
        }
    })



</script>

<style scoped lang='scss'>
    .header-section {
        border: 0px;
        width: 100%;
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1000;
        transition: background-color 0.2s ease, box-shadow 0.2s ease;
    }

    @media screen and (max-width: 1024px) {
        .mobileMenuOpen {
            background-color: rgba(100,100,100,0.98);
        }
    }

    /* 搜索框动画 */
    .search-input-container {
        background-color: transparent;
        transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        /* 弹性动画效果 */
        max-width: 0;
        opacity: 0;
        transform: translateX(20px);

        @media screen and (max-width: 1024px) {
            background-color: white;
        }
    }

    .search-expanded {
        max-width: 300px;
        width: 250px;
        opacity: 1;
        transform: translateX(0);
    }

    .search-collapsed {
        max-width: 0;
        width: 0;
        opacity: 0;
        transform: translateX(20px);
    }

    /* 覆盖浏览器默认聚焦样式 */
    input:focus {
        border-color: #059669 !important;
        /* green-600 */
        outline: none !important;
        box-shadow: 0 0 0 1px rgba(5, 150, 105, 0.2) !important;
    }

    /* 搜索输入框样式覆盖 */
    .search-input-container input {
        border-radius: 9999px 9999px 9999px 9999px !important;
        border-color: #059669 !important;
        /* green-600 */
    }

    /* 搜索按钮形状 */
    .search-button {
        border-radius: 9999px !important;

    }

    .search-expanded+.search-button {
        border-radius: 0 9999px 9999px 0 !important;
    }

    /* 导航链接下划线动画 */
    .nav-link {
        position: relative;
        transition: color 0.3s ease;

        &::after {
            content: '';
            position: absolute;
            bottom: -39px;
            left: 50%;
            right: 50%;
            height: 4px;
            background-color: #047857;
            /* green-600 */
            transition: all 0.3s ease;
            opacity: 0;
            margin-bottom: 2px;
        }

        &:hover::after {
            left: 0;
            right: 0;
            opacity: 1;
        }

        @media screen and (max-width: 1028px) {
            color:white;
            &:hover{
                font-weight: bold;
                color:#059669
            }
            &::after {
                bottom: -17px !important;
            }
        }
    }


    /* 当前活动链接样式 */
    .active-link::after {
        left: 0;
        right: 0;
        opacity: 1;
    }

    /* 针对不同布局的链接调整下划线位置 */
    nav .nav-link {
        padding-bottom: 2px;

        // &::after {
        //   bottom: -2px;
        // }
    }

    /* 子菜单链接下划线位置调整 */
    .group-hover\:block .nav-link {
        &::after {
            bottom: 0;
        }
    }

    /* 移动端菜单链接下划线位置调整 */
    .lg\:hidden .nav-link {
        display: inline-block;

        &::after {
            bottom: 0;
        }
    }

</style>
